<script setup lang="ts">
import { IconAlertTriangle } from '@tabler/icons-vue';
import { useCurrentElement } from '@vueuse/core';
import { ModalElement } from 'inclusive-elements';
import type { Component } from 'vue';
import { ref } from 'vue';

const props = withDefaults(
    defineProps<{
        title?: string;
        message?: string;
        type?: string;
        icon?: Component | null;
        confirmLabel?: string;
        cancelLabel?: string;
        onConfirm?: () => any | Promise<any>;
    }>(),
    {
        type: 'primary',
        confirmLabel: 'Confirm',
        cancelLabel: 'Cancel',
        icon: () => IconAlertTriangle,
    },
);

defineEmits(['close']);

const el = useCurrentElement<ModalElement>();
const loading = ref(false);

async function confirm() {
    loading.value = true;
    try {
        await props.onConfirm?.();
    } finally {
        loading.value = false;
    }
    el.value.close();
}
</script>

<template>
    <ui-modal class="modal--alert" open @close="$emit('close')" :static="loading">
        <div class="Dialog Dialog--alert">
            <div class="Dialog-body stack gap-lg align-center text-center wrap-balance">
                <component v-if="icon" :is="icon" class="icon text-xxl" :class="`color-${type}`" />

                <slot>
                    <div class="stack gap-sm">
                        <p class="h4">{{ title }}</p>
                        <p>{{ message }}</p>
                    </div>
                </slot>

                <div class="Dialog-alertActions align-self-stretch">
                    <button class="btn" @click="el.close()">{{ cancelLabel }}</button>

                    <button
                        class="btn"
                        @click="confirm"
                        :disabled="loading"
                        :class="[`bg-${type}`, { 'is-loading': loading }]"
                    >
                        {{ confirmLabel }}
                    </button>
                </div>
            </div>
        </div>
    </ui-modal>
</template>
